/**
 * Unified layer recommendation logic
 * Used by both frontend and backend for consistent recommendations
 */

/**
 * Enhanced v2.1 Physics-Based Layer Algorithm Functions
 * Implements 6+ factor weather analysis for precise layer recommendations
 */

/**
 * Calculate wind chill penalty effect
 * @param {number} feelsLike - Apparent temperature in Celsius
 * @param {number} windSpeed - Wind speed in km/h
 * @returns {number} Temperature adjustment in Celsius
 */
export const windPenalty = (feelsLike, windSpeed) => {
  if (windSpeed <= 5) return 0;
  
  // Enhanced wind chill calculation using Richardson formula
  const windEffect = Math.min(windSpeed / 3.6, 20); // Convert to m/s, cap at 20 m/s
  const baseEffect = Math.pow(windEffect, 0.16);
  
  if (feelsLike <= 10) {
    // Cold conditions: significant wind chill effect
    return -2.5 * baseEffect;
  } else if (feelsLike <= 20) {
    // Moderate conditions: moderate wind effect
    return -1.5 * baseEffect;
  } else {
    // Warm conditions: minimal effect, can be cooling
    return -0.5 * baseEffect;
  }
};

/**
 * Calculate precipitation comfort penalty
 * @param {number} rain - Rain rate in mm/h
 * @param {number} snow - Snow rate in mm/h
 * @returns {number} Temperature adjustment in Celsius
 */
export const rainPenalty = (rain, snow) => {
  const totalPrecip = rain + snow;
  if (totalPrecip <= 0.1) return 0;
  
  // Snow feels colder than rain
  const snowMultiplier = 1.5;
  const effectivePrecip = rain + (snow * snowMultiplier);
  
  if (effectivePrecip < 0.5) return -1;
  if (effectivePrecip < 2.0) return -2;
  if (effectivePrecip < 5.0) return -3;
  return -4; // Heavy precipitation
};

/**
 * Calculate damp chill effect from humidity
 * @param {number} humidity - Relative humidity percentage
 * @param {number} feelsLike - Apparent temperature in Celsius
 * @returns {number} Temperature adjustment in Celsius
 */
export const dampChill = (humidity, feelsLike) => {
  if (humidity < 60) return 0;
  
  const humidityEffect = (humidity - 60) / 40; // 0 to 1 scale above 60%
  
  if (feelsLike <= 5) {
    // Cold + humid = much colder feeling
    return -2.5 * humidityEffect;
  } else if (feelsLike <= 15) {
    // Cool + humid = moderately colder
    return -1.5 * humidityEffect;
  } else {
    // Warm + humid handled by hotHumidPenalty
    return 0;
  }
};

/**
 * Calculate hot humid discomfort penalty
 * @param {number} dewPoint - Dew point in Celsius
 * @param {number} feelsLike - Apparent temperature in Celsius
 * @returns {number} Temperature adjustment in Celsius
 */
export const hotHumidPenalty = (dewPoint, feelsLike) => {
  if (feelsLike <= 20 || dewPoint <= 15) return 0;
  
  // High dew point in warm conditions feels much hotter
  const dewEffect = Math.max(0, dewPoint - 15) / 10; // Scale from dew point 15°C+
  const tempEffect = Math.max(0, feelsLike - 20) / 15; // Scale from 20°C+
  
  return Math.min(4, dewEffect * tempEffect * 3); // Max +4°C penalty
};

/**
 * Calculate solar radiation warming boost
 * @param {number} cloudCover - Cloud cover percentage
 * @param {number} feelsLike - Apparent temperature in Celsius
 * @param {number} uvIndex - UV index
 * @returns {number} Temperature adjustment in Celsius
 */
export const solarBoost = (cloudCover, feelsLike, uvIndex) => {
  if (cloudCover >= 80 || uvIndex <= 1) return 0;
  
  const clearSkyFactor = Math.max(0, (100 - cloudCover) / 100);
  const uvFactor = Math.min(1, uvIndex / 6); // Scale to 0-1 based on UV 0-6+
  
  // Solar effect stronger in cooler conditions
  if (feelsLike <= 10) {
    return 3 * clearSkyFactor * uvFactor;
  } else if (feelsLike <= 20) {
    return 2 * clearSkyFactor * uvFactor;
  } else {
    return 1 * clearSkyFactor * uvFactor;
  }
};

/**
 * Calculate altitude adjustment (placeholder for future GPS integration)
 * @param {number} altitude - Altitude in meters
 * @returns {number} Temperature adjustment in Celsius
 */
export const altitudeBoost = (altitude = 0) => {
  // Standard atmospheric lapse rate: ~6.5°C per 1000m
  // For now, return 0 until GPS altitude is available
  return 0;
};

/**
 * Calculate metabolic heat from activity
 * @param {string} mode - Activity mode: 'walk', 'run', 'cycle', 'travel'
 * @param {number} intensity - Intensity level (1-3 for run/cycle)
 * @returns {number} Temperature adjustment in Celsius
 */
export const metabolicHeat = (mode = 'walk', intensity = 1) => {
  switch (mode.toLowerCase()) {
    case 'walk':
      return 3;
    case 'run':
      return [6, 8, 10][Math.min(2, Math.max(0, intensity - 1))];
    case 'cycle':
      return [5, 7, 9][Math.min(2, Math.max(0, intensity - 1))];
    case 'travel':
      return 0; // Passive travel
    default:
      return 3; // Default to walking
  }
};

/**
 * Köppen climate seeds for newcomer acclimation adjustments
 * Based on PRD specification - temperature adjustments in Celsius
 */
const KOPPEN_CLIMATE_SEEDS = {
  // Tropical climates - people accustomed to high heat
  'Af': -2.0,  // Tropical rainforest
  'Am': -2.0,  // Tropical monsoon
  'Aw': -2.0,  // Tropical savanna
  
  // Arid climates - accustomed to dry heat/cold
  'BWh': -1.0, // Hot desert
  'BWk': -1.0, // Cold desert
  'BSh': -1.0, // Hot semi-arid
  'BSk': -1.0, // Cold semi-arid
  
  // Temperate climates - moderate adaptation needs
  'Csa': -1.5, // Mediterranean hot summer
  'Csb': -1.5, // Mediterranean warm summer
  'Cfa': -1.0, // Humid subtropical
  'Cfb': -1.0, // Oceanic
  
  // Continental climates - accustomed to temperature extremes
  'Dfa': 0.0,  // Hot summer humid continental
  'Dfb': 0.0,  // Warm summer humid continental
  'Dfc': 0.0,  // Subarctic
  
  // Polar climates - accustomed to extreme cold
  'ET': 1.0,   // Tundra
  'EF': 2.0    // Ice cap
};

/**
 * Calculate months between two dates (arrival month vs current)
 * @param {string} arrivalMonth - YYYY-MM format
 * @returns {number} Number of months since arrival
 */
const calculateMonthsSince = (arrivalMonth) => {
  if (!arrivalMonth) return 0;
  
  const now = new Date();
  const [year, month] = arrivalMonth.split('-').map(Number);
  const arrivalDate = new Date(year, month - 1); // month is 0-indexed
  
  const yearDiff = now.getFullYear() - arrivalDate.getFullYear();
  const monthDiff = now.getMonth() - arrivalDate.getMonth();
  
  return Math.max(0, yearDiff * 12 + monthDiff);
};

/**
 * Calculate newcomer acclimation adjustment based on climate transition
 * Implements PRD section 6.3C newcomer logic
 * @param {object} userPrefs - User preferences with newcomer data
 * @param {number} currentTemp - Current location temperature (T70_curr equivalent)
 * @param {string} currentKoppen - Current location Köppen classification
 * @returns {number} Temperature adjustment in Celsius (-2 to +2)
 */
export const newcomerAdjustment = (userPrefs = {}, currentTemp = 0, currentKoppen = '') => {
  const {
    home_T30 = null,
    home_koppen = null,
    arrival_month = null
  } = userPrefs;
  
  // Return 0 if no newcomer data available
  if (!arrival_month || home_T30 === null || !home_koppen) {
    return 0;
  }
  
  // Get climate seeds for home and current locations
  const homeSeed = KOPPEN_CLIMATE_SEEDS[home_koppen] || 0;
  const currentSeed = KOPPEN_CLIMATE_SEEDS[currentKoppen] || 0;
  
  // Calculate normalized temperatures with climate adjustments
  const homeNorm = home_T30 + homeSeed;
  const currentNorm = currentTemp + currentSeed;
  
  // Calculate climate differential (negative = moved to colder climate)
  const climateDiff = currentNorm - homeNorm;
  
  // Initial adjustment: ~6°C per layer as per PRD
  const initialShift = Math.max(-2, Math.min(2, Math.round((-climateDiff) / 6)));
  
  // Calculate temporal decay with 6-month half-life
  const monthsHere = calculateMonthsSince(arrival_month);
  const decay = Math.exp(-Math.log(2) * monthsHere / 6);
  
  // Apply decay and truncate to integer
  const finalAdjustment = Math.trunc(initialShift * decay);
  
  return Math.max(-2, Math.min(2, finalAdjustment));
};

/**
 * Enhanced v2.1 layer recommendation logic with physics-based algorithm
 * @param {number} temperature - The apparent temperature in Celsius
 * @param {number} precipitation - Precipitation amount (default: 0)
 * @param {number} windSpeed - Wind speed in km/h (default: 0)
 * @param {boolean} includeBackground - Whether to include background colors for UI (default: false)
 * @param {object} weatherData - Enhanced weather parameters (v2.1)
 * @param {object} userPrefs - User preferences and activity mode
 * @returns {object} Recommendation object with description, layers, emojis, and optional background
 */
export const getLayerRecommendation = (temperature, precipitation = 0, windSpeed = 0, includeBackground = false, weatherData = {}, userPrefs = {}) => {
  // Extract enhanced weather data and user preferences
  const {
    rain = 0,
    snowfall = 0,
    relativeHumidity = 50,
    cloudCover = 50,
    dewPoint = 0,
    uvIndex = 0,
    altitude = 0,
    currentKoppen = '' // Current location's Köppen classification
  } = weatherData;
  
  const {
    mode = 'walk',
    intensity = 1,
    layer_shift = 0,
    vote_shift = 0,
    // Newcomer acclimation fields
    home_T30 = null,
    home_koppen = null,
    arrival_month = null
  } = userPrefs;

  // Calculate physics-based adjustments
  const windEffect = windPenalty(temperature, windSpeed);
  const precipEffect = rainPenalty(rain, snowfall);
  const dampEffect = dampChill(relativeHumidity, temperature);
  const humidEffect = hotHumidPenalty(dewPoint, temperature);
  const solarEffect = solarBoost(cloudCover, temperature, uvIndex);
  const altEffect = altitudeBoost(altitude);
  const metabolicEffect = metabolicHeat(mode, intensity);
  
  // Calculate newcomer acclimation adjustment
  const newcomerEffect = newcomerAdjustment(userPrefs, temperature, currentKoppen);
  
  // Apply physics-based temperature adjustment
  const adjustedTemp = temperature 
    + windEffect 
    + precipEffect 
    + dampEffect 
    + humidEffect 
    + solarEffect 
    + altEffect 
    + metabolicEffect
    + layer_shift     // Personal preference adjustment
    + vote_shift      // Community feedback adjustment
    + newcomerEffect; // Climate acclimation adjustment

  let recommendation = { description: '', layers: 0, emojis: [] };

  // Enhanced temperature-based logic with physics adjustments
  if (adjustedTemp > 20) {
    recommendation = { description: 'T-shirt weather', layers: 1, emojis: ['👕'] };
  } else if (adjustedTemp > 17) {
    recommendation = { description: 'T-shirt weather', layers: 1, emojis: ['👕'] };
  } else if (adjustedTemp >= 15 && adjustedTemp <= 17) {
    if (windSpeed > 20) {
      recommendation = { description: 'Light jacket weather - windy', layers: 2, emojis: ['👕', '🧥'] };
    } else {
      recommendation = { description: 'T-shirt weather', layers: 1, emojis: ['👕'] };
    }
  } else if (adjustedTemp > 7 && adjustedTemp <= 15) {
    recommendation = { description: 'Light jacket weather', layers: 2, emojis: ['👕', '🧥'] };
  } else if (adjustedTemp > -5 && adjustedTemp <= 7) {
    recommendation = { description: 'Sweater and jacket weather', layers: 3, emojis: ['👕', '🧥', '🧣'] };
  } else if (adjustedTemp >= -17 && adjustedTemp <= -5) {
    recommendation = { description: 'Winter jacket weather', layers: 4, emojis: ['👕', '🧥', '🧣', '👞'] };
  } else { // adjustedTemp < -17
    recommendation = { description: 'Full winter gear weather', layers: 5, emojis: ['👕', '🧥', '🧣', '🧤', '👞'] };
  }
  
  // Add activity mode context to description
  if (mode !== 'walk') {
    const activityMap = {
      'run': 'running',
      'cycle': 'cycling', 
      'travel': 'travel'
    };
    recommendation.description += ` (${activityMap[mode] || mode})`;
  }

  // Add precipitation emoji if there's rain or snow
  if (precipitation > 0) {
    recommendation.emojis.push('☂️');
  }

  // Add wind emoji consistently if windy
  if (windSpeed > 20) {
    recommendation.emojis.push('🌬️');
  }

  // Add background colors only for frontend UI
  if (includeBackground) {
    if (adjustedTemp <= -17) {
      recommendation.background = 'bg-purple-50 dark:bg-purple-900';
    } else if (adjustedTemp > 17) {
      recommendation.background = 'bg-yellow-50 dark:bg-yellow-900';
    } else if (adjustedTemp >= 15 && adjustedTemp <= 17 && windSpeed > 10) {
      recommendation.background = 'bg-blue-100 dark:bg-blue-900';
    } else if (adjustedTemp >= 15 && adjustedTemp <= 17) {
      recommendation.background = 'bg-yellow-50 dark:bg-yellow-900';
    } else if (adjustedTemp > 7 && adjustedTemp <= 15) {
      recommendation.background = 'bg-blue-50 dark:bg-blue-900';
    } else if (adjustedTemp > -5 && adjustedTemp <= 7) {
      recommendation.background = 'bg-indigo-50 dark:bg-indigo-900';
    } else {
      recommendation.background = 'bg-purple-50 dark:bg-purple-900';
    }

    if (precipitation > 0) {
      recommendation.background += ' bg-opacity-75 dark:bg-opacity-75';
    }
  }

  // Add physics calculation details for debugging (v2.1)
  recommendation.physicsDetails = {
    originalTemp: temperature,
    adjustedTemp: adjustedTemp,
    adjustments: {
      wind: windEffect,
      precipitation: precipEffect,
      humidity: dampEffect,
      dewPoint: humidEffect,
      solar: solarEffect,
      altitude: altEffect,
      metabolic: metabolicEffect,
      personalBias: layer_shift,
      voteBias: vote_shift,
      newcomerAcclimation: newcomerEffect
    },
    totalAdjustment: adjustedTemp - temperature,
    algorithmVersion: 'v2.1-physics-newcomer'
  };

  return recommendation;
};

/**
 * Validate temperature range
 * @param {number} temperature - Temperature in Celsius
 * @returns {boolean} Whether temperature is in valid range
 */
export const isValidTemperature = (temperature) => {
  return !isNaN(temperature) && temperature >= -50 && temperature <= 50;
};

/**
 * Convert Fahrenheit to Celsius
 * @param {number} fahrenheit - Temperature in Fahrenheit
 * @returns {number} Temperature in Celsius
 */
export const convertToCelsius = (fahrenheit) => {
  return (fahrenheit - 32) * 5 / 9;
};

/**
 * Convert Celsius to Fahrenheit
 * @param {number} celsius - Temperature in Celsius
 * @returns {number} Temperature in Fahrenheit
 */
export const convertToFahrenheit = (celsius) => {
  return (celsius * 9 / 5) + 32;
};